import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface TicketStatusManagerProps {
  currentStatus: string;
  ticketId: string;
  onStatusUpdate: (status: string, reason?: string) => void;
  disabled?: boolean;
}

export function TicketStatusManager({ currentStatus, ticketId, onStatusUpdate, disabled }: TicketStatusManagerProps) {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [pauseReason, setPauseReason] = useState('');
  const [showReasonField, setShowReasonField] = useState(false);

  const statusOptions = [
    { value: 'open', label: 'Aberto', variant: 'destructive' as const },
    { value: 'in_progress', label: 'Em Andamento', variant: 'default' as const },
    { value: 'paused', label: 'Pausado', variant: 'secondary' as const },
    { value: 'resolved', label: 'Resolvido', variant: 'default' as const },
    { value: 'closed', label: 'Fechado', variant: 'outline' as const },
  ];

  const handleStatusChange = (newStatus: string) => {
    setSelectedStatus(newStatus);
    if (newStatus === 'paused') {
      setShowReasonField(true);
    } else {
      setShowReasonField(false);
      setPauseReason('');
    }
  };

  const handleUpdate = () => {
    if (selectedStatus === 'paused' && !pauseReason.trim()) {
      return; // Don't allow empty reason for paused status
    }
    
    onStatusUpdate(selectedStatus, selectedStatus === 'paused' ? pauseReason : undefined);
    setShowReasonField(false);
    setPauseReason('');
  };

  const currentStatusLabel = statusOptions.find(option => option.value === currentStatus)?.label || currentStatus;
  const currentStatusVariant = statusOptions.find(option => option.value === currentStatus)?.variant || 'outline';

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium">Status Atual</Label>
        <div className="mt-1">
          <Badge variant={currentStatusVariant}>{currentStatusLabel}</Badge>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <Label htmlFor="status-select" className="text-sm font-medium">
            Alterar Status
          </Label>
          <Select
            value={selectedStatus}
            onValueChange={handleStatusChange}
            disabled={disabled}
          >
            <SelectTrigger id="status-select" className="mt-1">
              <SelectValue placeholder="Selecione um status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex items-center gap-2">
                    <Badge variant={option.variant} className="text-xs">
                      {option.label}
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showReasonField && (
          <div>
            <Label htmlFor="pause-reason" className="text-sm font-medium text-destructive">
              Motivo da Pausa *
            </Label>
            <Textarea
              id="pause-reason"
              placeholder="Explique o motivo para pausar este chamado..."
              value={pauseReason}
              onChange={(e) => setPauseReason(e.target.value)}
              className="mt-1"
              rows={3}
            />
            {!pauseReason.trim() && (
              <p className="text-xs text-destructive mt-1">
                O motivo é obrigatório para pausar um chamado
              </p>
            )}
          </div>
        )}

        {selectedStatus !== currentStatus && (
          <Button
            onClick={handleUpdate}
            disabled={disabled || (selectedStatus === 'paused' && !pauseReason.trim())}
            className="w-full"
          >
            Atualizar Status
          </Button>
        )}
      </div>
    </div>
  );
}